/**
 * Bridge to the "8080.AI Snap & Share" chrome extension.
 *
 * Discovers the ID of the extension currently injected into the page, then
 * uses chrome.runtime.sendMessage(extensionId, message). The configured Web
 * Store ID remains a fallback for older published extension builds.
 */

export const EXTENSION_ID = process.env.REACT_APP_SHARE_EXTENSION_ID || 'REPLACE_WITH_STORE_ID';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PING_TIMEOUT_MS = 2000;
const DISCOVERY_TIMEOUT_MS = 500;
const WEB_BRIDGE_SOURCE = '8080-ai-web';
const EXTENSION_BRIDGE_SOURCE = '8080-ai-extension';
const DISCOVER_EXTENSION = 'DISCOVER_EXTENSION';
const EXTENSION_READY = 'EXTENSION_READY';
// Must exceed the extension's SHARE_REEL budget: tab load, delayed composer
// rendering, bounded AI guidance, media attachment, and up to 5 minutes for
// the user to click Post. 9 minutes keeps the response channel open with margin.
const SHARE_TIMEOUT_MS = 9 * 60 * 1000;
const CHECK_VIEWS_TIMEOUT_MS = 2 * 60 * 1000;
const LOOP_PAIR_TIMEOUT_MS = 30 * 1000;

export interface LoopBrowserStatus {
  installed: boolean;
  paired: boolean;
  connected: boolean;
  paused: boolean;
  profile_id: string | null;
  name: string;
  api_origin: string;
  one_click_pairing?: boolean;
  revoked_pairing_recovery?: boolean;
}

export interface PairLoopBrowserRequest {
  pairingCode: string;
  name: string;
  apiOrigin: string;
}

export interface ReconnectLoopBrowserRequest {
  profileId: string;
  apiOrigin: string;
}

export interface LoopBrowserPairResult {
  ok: true;
  paired: true;
  profile_id: string;
}

interface ChromeRuntimeLike {
  sendMessage: (extensionId: string, message: unknown, callback: (response?: unknown) => void) => void;
  lastError?: { message?: string };
}

interface ChromeWindowLike {
  chrome?: {
    runtime?: Partial<ChromeRuntimeLike>;
  };
}

interface ExtensionAnnouncement {
  source?: string;
  type?: string;
  nonce?: string;
  extensionId?: string;
}

export interface ShareReelRequest {
  shareId: string;
  platform: string;
  videoUrl: string;
  caption: string;
  automationToken?: string;
  autoSubmit: boolean;
  openInBackground?: boolean;
}

export interface ShareReelResult {
  ok: boolean;
  reelUrl?: string;
  error?: string;
}

export interface CheckViewsRequest {
  shareId: string;
  platform: string;
  reelUrl: string;
}

export interface CheckViewsResult {
  ok: boolean;
  views?: number;
  linkAlive?: boolean;
  error?: string;
}

export const normalizeCheckViewsResult = (result: CheckViewsResult): CheckViewsResult => {
  if (!result.ok) return result;
  if (result.linkAlive === false) return { ok: true, views: 0, linkAlive: false };
  const validLiveCount = (
    result.linkAlive === true
    && typeof result.views === 'number'
    && Number.isSafeInteger(result.views)
    && result.views >= 0
  );

  if (validLiveCount) return result;

  return { ok: false, error: 'The extension returned invalid view data. Please try again.' };
};

/** Convert an API-relative path (e.g. ShareItem.video_url) into an absolute URL for the extension. */
export const toAbsoluteUrl = (path: string): string => (/^https?:\/\//.test(path) ? path : `${API_BASE_URL}${path}`);

const getChromeRuntime = (): ChromeRuntimeLike | null => {
  if (typeof window === 'undefined') return null;

  const runtime = (window as unknown as ChromeWindowLike).chrome?.runtime;

  return runtime && typeof runtime.sendMessage === 'function' ? (runtime as ChromeRuntimeLike) : null;
};

let discoveredExtensionId: string | null = null;

const isExtensionId = (value: unknown): value is string => typeof value === 'string' && /^[a-p]{32}$/.test(value);

const discoverExtensionId = (): Promise<string | null> => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (discoveredExtensionId) return Promise.resolve(discoveredExtensionId);

  return new Promise((resolve) => {
    const nonce = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    function finish(extensionId: string | null): void {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve(extensionId);
    }

    function onMessage(event: MessageEvent<ExtensionAnnouncement>): void {
      const data = event.data;

      if (
        event.source !== window ||
        event.origin !== window.location.origin ||
        data?.source !== EXTENSION_BRIDGE_SOURCE ||
        data.type !== EXTENSION_READY ||
        data.nonce !== nonce ||
        !isExtensionId(data.extensionId)
      ) {
        return;
      }

      discoveredExtensionId = data.extensionId;
      finish(discoveredExtensionId);
    }

    window.addEventListener('message', onMessage);

    const timer = window.setTimeout(() => finish(null), DISCOVERY_TIMEOUT_MS);

    window.postMessage({ source: WEB_BRIDGE_SOURCE, type: DISCOVER_EXTENSION, nonce }, window.location.origin);
  });
};

const resolveExtensionId = async (): Promise<string | null> => {
  const discovered = await discoverExtensionId();

  return discovered || (isExtensionId(EXTENSION_ID) ? EXTENSION_ID : null);
};

const errorText = (err: unknown): string => (err instanceof Error ? err.message : 'Extension call failed');

// Chrome's answers for a message that never reached an extension: it was removed, or reinstalled under a new ID.
const UNREACHABLE = /Receiving end does not exist|Could not establish connection|Invalid extension id/i;

/** A delivery failure is marked so it can be resent; a timeout or missing reply may already have acted. */
const deliveryError = (text: string): Error => Object.assign(new Error(text), { unreachable: UNREACHABLE.test(text) });

const isUnreachable = (error: unknown): boolean => Boolean((error as { unreachable?: boolean } | null)?.unreachable);

const dispatchMessage = (
  runtime: ChromeRuntimeLike,
  extensionId: string,
  message: Record<string, unknown>,
  timeoutMs: number,
  resolve: (value: unknown) => void,
  reject: (reason: Error) => void
): void => {
  const timer = window.setTimeout(() => reject(new Error('Extension request timed out')), timeoutMs);

  runtime.sendMessage(extensionId, message, (response) => {
    window.clearTimeout(timer);
    const lastError = runtime.lastError;

    if (lastError || response === undefined) {
      reject(deliveryError(lastError?.message || 'Extension did not respond'));

      return;
    }

    resolve(response);
  });
};

async function sendOnce<T>(message: Record<string, unknown>, timeoutMs: number): Promise<T> {
  const extensionId = await resolveExtensionId();
  const runtime = getChromeRuntime();

  if (!runtime || !extensionId) {
    throw new Error('Extension not available');
  }

  return new Promise<T>((resolve, reject) => {

    try {
      dispatchMessage(runtime, extensionId, message, timeoutMs, resolve as (value: unknown) => void, reject);
    } catch (err) {
      reject(deliveryError(errorText(err)));
    }
  });
}

async function sendExtensionMessage<T>(message: Record<string, unknown>, timeoutMs: number): Promise<T> {
  try {
    return await sendOnce<T>(message, timeoutMs);
  } catch (error) {
    // The remembered extension is gone (removed, or reinstalled under a new ID) and the message never arrived:
    // find the extension on the page again and send once more.
    if (!isUnreachable(error) || !discoveredExtensionId) throw error;
    discoveredExtensionId = null;

    return sendOnce<T>(message, timeoutMs);
  }
}

/** True when the extension is installed and answering PING messages. */
export const pingExtension = async (): Promise<boolean> => {
  try {
    const response = await sendExtensionMessage<{ ok?: boolean }>({ action: 'PING' }, PING_TIMEOUT_MS);

    return Boolean(response && response.ok);
  } catch {
    return false;
  }
};

/** The installed extension's version, from the PING every version answers; null when none is reachable. */
export const getExtensionVersion = async (): Promise<string | null> => {
  try {
    const response = await sendExtensionMessage<{ ok?: boolean; version?: unknown }>({ action: 'PING' }, PING_TIMEOUT_MS);

    return response?.ok && typeof response.version === 'string' && /^\d+(\.\d+){0,3}$/.test(response.version)
      ? response.version : null;
  } catch {
    return null;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const isLoopBrowserStatus = (value: unknown): value is LoopBrowserStatus & { ok: true } => (
  isRecord(value) && value.ok === true && value.installed === true
  && typeof value.paired === 'boolean' && typeof value.connected === 'boolean'
  && typeof value.paused === 'boolean' && typeof value.name === 'string'
  && typeof value.api_origin === 'string'
  && (value.profile_id === null || typeof value.profile_id === 'string')
  && (!value.paired || (typeof value.profile_id === 'string' && value.profile_id.length > 0))
  && (value.one_click_pairing === undefined || typeof value.one_click_pairing === 'boolean')
  && (value.revoked_pairing_recovery === undefined || typeof value.revoked_pairing_recovery === 'boolean')
);

/** Discover this browser's connection without disclosing extension credentials. */
export const getLoopBrowserStatus = async (): Promise<LoopBrowserStatus | null> => {
  try {
    const response = await sendExtensionMessage<unknown>({ action: 'LOOP_STATUS' }, PING_TIMEOUT_MS);

    if (!isLoopBrowserStatus(response)) return null;

    return {
      installed: response.installed, paired: response.paired, connected: response.connected,
      paused: response.paused, profile_id: response.profile_id, name: response.name,
      api_origin: response.api_origin,
      ...(response.one_click_pairing === undefined ? {} : { one_click_pairing: response.one_click_pairing }),
      ...(response.revoked_pairing_recovery === undefined ? {} : {
        revoked_pairing_recovery: response.revoked_pairing_recovery,
      }),
    };
  } catch {
    return null;
  }
};

const loopTransportError = (error: unknown): Error => {
  const message = errorText(error);

  if (message === 'Extension request timed out') {
    return new Error('The browser connection request timed out. Check the connection and try again.');
  }
  if (message === 'Extension not available' || message === 'Extension did not respond') {
    return new Error('The browser extension is unavailable. Install or update it, then refresh this page.');
  }

  return new Error(message);
};

const sendLoopBrowserMessage = async (message: Record<string, unknown>): Promise<Record<string, unknown>> => {
  const response = await sendExtensionMessage<unknown>(message, LOOP_PAIR_TIMEOUT_MS).catch((error: unknown) => {
    throw loopTransportError(error);
  });

  if (isRecord(response) && response.ok === true) return response;
  if (isRecord(response) && typeof response.error === 'string' && response.error.trim()) {
    throw new Error(response.error);
  }

  throw new Error('The extension returned an invalid response. Update it, refresh this page, and try again.');
};

const requirePairedBrowser = (response: Record<string, unknown>): LoopBrowserPairResult => {
  if (response.paired !== true || typeof response.profile_id !== 'string' || !response.profile_id.trim()) {
    throw new Error('The extension could not confirm browser pairing. Check the connection and try again.');
  }

  return { ok: true, paired: true, profile_id: response.profile_id };
};

/** Pair the extension in this browser directly; no credential-bearing options URL is opened. */
export const pairLoopBrowser = async (request: PairLoopBrowserRequest): Promise<LoopBrowserPairResult> => (
  requirePairedBrowser(await sendLoopBrowserMessage({
    action: 'LOOP_PAIR', pairingCode: request.pairingCode, name: request.name, apiOrigin: request.apiOrigin,
  }))
);

/** Resume only the browser profile the caller has verified belongs to this account. */
export const reconnectLoopBrowser = async (request: ReconnectLoopBrowserRequest): Promise<LoopBrowserPairResult> => {
  const result = requirePairedBrowser(await sendLoopBrowserMessage({
    action: 'LOOP_RECONNECT', profileId: request.profileId, apiOrigin: request.apiOrigin,
  }));

  if (result.profile_id !== request.profileId) {
    throw new Error('The browser pairing changed. Refresh this page and check the connected browser.');
  }

  return result;
};

/** Clear only the expected local pairing after the account has revoked that profile. */
export const forgetLoopBrowser = async (request: ReconnectLoopBrowserRequest): Promise<void> => {
  const response = await sendLoopBrowserMessage({
    action: 'LOOP_FORGET', profileId: request.profileId, apiOrigin: request.apiOrigin,
  });

  if (response.paired !== false || response.profile_id !== null) {
    throw new Error('The extension could not confirm disconnection. Refresh this page and try connecting again.');
  }
};

/** Open extension settings without sending pairing hashes or credentials. */
export const openLoopBrowserOptions = async (): Promise<void> => {
  await sendLoopBrowserMessage({ action: 'LOOP_OPEN_OPTIONS' });
};

/** Ask the extension to open the platform composer and attach the reel. */
export const shareReel = async (request: ShareReelRequest): Promise<ShareReelResult> => {
  try {
    return await sendExtensionMessage<ShareReelResult>(
      { action: 'SHARE_REEL', apiBaseUrl: API_BASE_URL, ...request },
      SHARE_TIMEOUT_MS,
    );
  } catch (err) {
    return { ok: false, error: errorText(err) };
  }
};

/** Ask the extension to open the posted reel and read its view count. */
export const checkViews = async (request: CheckViewsRequest): Promise<CheckViewsResult> => {
  try {
    const result = await sendExtensionMessage<CheckViewsResult>(
      { action: 'CHECK_VIEWS', openInBackground: true, ...request },
      CHECK_VIEWS_TIMEOUT_MS,
    );

    return normalizeCheckViewsResult(result);
  } catch (err) {
    return { ok: false, error: errorText(err) };
  }
};
